import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Terminal,
} from "lucide-react";
import type { ReactNode } from "react";
import { FinalCta } from "../components/FinalCta";
import { ScrollReveal } from "../components/ScrollReveal";
import { ToolLogoStrip, WorksWithSection } from "../components/WorksWithSection";

const proofKeys = ["payment", "openai", "sellers", "models"] as const;

export default function Landing() {
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-full overflow-x-clip bg-[var(--color-page)]">
      {/* 1. Hero — outcome claim + install path.
       * Section order: hero → benefits → proof screenshots → tools → install. */}
      <section className="hero-compute-bg relative overflow-hidden border-b border-[var(--color-line)] py-12 md:py-16 xl:py-20">
        <div className="hero-container relative grid items-start gap-10 lg:grid-cols-[minmax(0,0.92fr)_minmax(420px,0.78fr)] xl:grid-cols-[minmax(0,0.95fr)_minmax(520px,0.85fr)]">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] px-3 py-1.5 text-[12px] font-bold text-[var(--color-primary-deep)] shadow-[0_10px_24px_rgba(60,41,112,0.06)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-success)] pulse-dot" />
              {t("landing.hero.eyebrow")}
            </div>
            <h1 className="mt-6 text-[40px] font-bold leading-[1.02] tracking-[-0.01em] text-[var(--color-ink-strong)] text-balance sm:text-[52px] md:text-[64px] xl:text-[80px]">
              {t("landing.hero.title")}
            </h1>
            <p className="mt-6 max-w-[60ch] text-[16px] leading-7 text-[var(--color-text)] text-pretty md:text-[18px]">
              {t("landing.hero.lede")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#install"
                className="inline-flex h-11 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-primary)] px-5 text-[14px] font-bold text-white shadow-[0_16px_34px_rgba(124,61,240,0.18)] transition-transform hover:-translate-y-0.5 active:scale-[0.98] sm:h-12 sm:px-6"
              >
                <Terminal className="h-4 w-4" aria-hidden="true" />
                {t("landing.hero.primaryCta")}
              </a>
              <Link
                to="/docs"
                className="inline-flex h-11 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-panel)] px-5 text-[14px] font-bold text-[var(--color-ink-mid)] transition-colors hover:bg-[var(--color-panel-subtle)] sm:h-12 sm:px-6"
              >
                <BookOpen className="h-4 w-4" aria-hidden="true" />
                {t("landing.hero.secondaryCta")}
              </Link>
            </div>

            <ul
              className="num mt-8 flex flex-wrap gap-2"
              aria-label={t("landing.hero.proofsLabel")}
            >
              {proofKeys.map((key) => (
                <li
                  key={key}
                  className="inline-flex items-center rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] px-2.5 py-1 text-[12px] font-bold text-[var(--color-muted)]"
                >
                  {t(`landing.hero.proofs.${key}`)}
                </li>
              ))}
            </ul>
          </div>

          <div className="hero-proof-panel min-w-0 self-start lg:pl-2 xl:pl-6">
            <article className="lift overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] shadow-[0_24px_70px_rgba(60,41,112,0.13)]">
              <div className="screenshot-frame bg-[var(--color-page-soft)] p-2">
                <picture>
                  <source
                    srcSet="/screenshots/overview.webp"
                    type="image/webp"
                  />
                  <img
                    src="/screenshots/overview.png"
                    alt={t("landing.screenshots.overview.title")}
                    width={1440}
                    height={1000}
                    fetchPriority="high"
                    decoding="async"
                    className="block w-full rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel)] shadow-sm"
                  />
                </picture>
              </div>
            </article>
            <div className="mt-5 rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)]/88 p-3 shadow-[0_12px_30px_rgba(60,41,112,0.07)]">
              <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-muted)]">
                {t("landing.hero.toolsLabel")}
              </p>
              <ToolLogoStrip compact />
            </div>
          </div>
        </div>
      </section>

      {/* 2-4. Benefits — separate the three user outcomes. */}
      <BenefitSection
        sectionKey="time"
        eyebrow={t("landing.benefits.items.time.eyebrow")}
        title={t("landing.benefits.items.time.title")}
        body={t("landing.benefits.items.time.body")}
        points={
          t("landing.benefits.items.time.points", {
            returnObjects: true,
          }) as unknown as string[]
        }
        visual={
          <ScrollReveal variant="visual" delay={120} className="space-y-4">
            <ProductShot
              src="/screenshots/overview.png"
              title={t("landing.screenshots.overview.title")}
              priority
            />
            <div className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] p-3 shadow-[0_12px_30px_rgba(60,41,112,0.05)]">
              <ToolLogoStrip />
            </div>
          </ScrollReveal>
        }
        borderTop
        muted
      />

      <BenefitSection
        sectionKey="money"
        eyebrow={t("landing.benefits.items.money.eyebrow")}
        title={t("landing.benefits.items.money.title")}
        body={t("landing.benefits.items.money.body")}
        points={
          t("landing.benefits.items.money.points", {
            returnObjects: true,
          }) as unknown as string[]
        }
        visual={
          <ScrollReveal variant="visual" delay={120}>
          <ProductShot
            src="/screenshots/routing.png"
            title={t("landing.screenshots.routing.title")}
            priority
          />
          </ScrollReveal>
        }
        reverse
      />

      <BenefitSection
        sectionKey="carefree"
        eyebrow={t("landing.benefits.items.carefree.eyebrow")}
        title={t("landing.benefits.items.carefree.title")}
        body={t("landing.benefits.items.carefree.body")}
        points={
          t("landing.benefits.items.carefree.points", {
            returnObjects: true,
          }) as unknown as string[]
        }
        visual={
          <ScrollReveal variant="visual" delay={120}>
          <ProductShot
            src="/screenshots/ledger.png"
            title={t("landing.screenshots.ledger.title")}
            priority
          />
          </ScrollReveal>
        }
        borderTop
        muted
      />

      {/* 5. Works with — client compatibility. */}
      <WorksWithSection />

      {/* 6. Final install CTA. The user has read the story; this is the ask. */}
      <section
        id="install"
        className="section-anchor border-y border-[var(--color-line)] bg-[var(--color-page-soft)] py-12 md:py-16"
      >
        <FinalCta />
      </section>
    </div>
  );
}

function BenefitSection({
  sectionKey,
  eyebrow,
  title,
  body,
  points,
  visual,
  reverse = false,
  borderTop = false,
  muted = false,
}: {
  sectionKey: string;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  visual: ReactNode;
  reverse?: boolean;
  borderTop?: boolean;
  muted?: boolean;
}) {
  return (
    <ScrollReveal
      as="section"
      id={sectionKey}
      className={[
        "section-anchor py-12 md:py-16",
        borderTop ? "border-y border-[var(--color-line)]" : "",
        muted ? "bg-[var(--color-panel-subtle)]" : "bg-[var(--color-page)]",
      ].join(" ")}
    >
      <div
        className={[
          "page-container grid items-start gap-8 lg:gap-12",
          reverse
            ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,0.42fr)]"
            : "lg:grid-cols-[minmax(0,0.42fr)_minmax(0,1fr)]",
          reverse ? "lg:grid-flow-col-dense" : "",
        ].join(" ")}
      >
        <div className={reverse ? "max-w-[440px] lg:col-start-2" : "max-w-[440px]"}>
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--color-primary-deep)]">
            {eyebrow}
          </p>
          <h2 className="marketing-heading mt-2 text-[32px] font-bold leading-[1.08] tracking-[-0.01em] text-[var(--color-ink-strong)] text-balance md:text-[44px] xl:text-[48px]">
            {title}
          </h2>
          <p className="mt-4 max-w-[42ch] text-[15px] leading-6 text-[var(--color-text)] text-pretty md:text-[16px] md:leading-7">
            {body}
          </p>
          <ul className="mt-6 space-y-3 border-t border-[var(--color-line)] pt-5">
            {points.map((point) => (
              <li
                key={point}
                className="flex items-start gap-2 text-[13.5px] font-bold leading-5 text-[var(--color-primary-deep)] text-pretty"
              >
                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={reverse ? "lg:col-start-1 lg:row-start-1" : ""}>
          {visual}
        </div>
      </div>
    </ScrollReveal>
  );
}

function ProductShot({
  src,
  title,
  className = "",
  priority = false,
}: {
  src: string;
  title: string;
  className?: string;
  priority?: boolean;
}) {
  return (
    <article
      className={[
        "lift overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] shadow-[0_12px_36px_rgba(60,41,112,0.07)]",
        className,
      ].join(" ")}
    >
      <div className="screenshot-frame bg-[var(--color-page-soft)] p-2">
        <picture>
          <source
            srcSet={src.replace(/\.png$/, ".webp")}
            type="image/webp"
          />
          <img
            src={src}
            alt={title}
            width={1440}
            height={1000}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            className="block w-full rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-panel)] shadow-sm"
          />
        </picture>
      </div>
    </article>
  );
}
